import { Dispatch, SetStateAction, useCallback, useState } from 'react';

type UseLocalStorageStateOptions<T> = {
  serialize: (value: T) => string;
  deserialize: (raw: string) => T;
};

export function useLocalStorageState<T>(
  key: string,
  getDefaultValue: () => T,
  options: UseLocalStorageStateOptions<T>
): [T, Dispatch<SetStateAction<T>>] {
  const { serialize, deserialize } = options;

  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return getDefaultValue();
      return deserialize(raw);
    } catch {
      return getDefaultValue();
    }
  });

  const setPersistedState = useCallback<Dispatch<SetStateAction<T>>>(
    (valueOrUpdater) => {
      setState((prev) => {
        const next =
          typeof valueOrUpdater === 'function'
            ? (valueOrUpdater as (p: T) => T)(prev)
            : valueOrUpdater;
        try {
          localStorage.setItem(key, serialize(next));
        } catch {
          // ignore localStorage write errors (quota, privacy mode, etc.)
        }
        return next;
      });
    },
    [key, serialize]
  );

  return [state, setPersistedState];
}

